/**
 * Outlet Service
 * Provides API calls for outlet management
 */

import {
  ApiResponse as APIResponse,
  SubscriptionPackage,
} from '../../shared/types';
import {
  CreateOutletRequest,
  Outlet,
  OutletLimits,
  OutletWithStats,
  UpdateOutletRequest,
} from '../types/outlet';
import { supabase } from './supabase';

// Package limits for outlets
const PACKAGE_OUTLET_LIMITS: Record<SubscriptionPackage, number | null> = {
  starter: 1,
  professional: 3,
  enterprise: null, // Unlimited
  [SubscriptionPackage.PRO_SEMI_ANNUAL]: 5,
};

// Package limits for users
const PACKAGE_USER_LIMITS: Record<SubscriptionPackage, number | null> = {
  starter: 5,
  professional: 20,
  enterprise: null, // Unlimited
  [SubscriptionPackage.PRO_SEMI_ANNUAL]: 50,
};

/**
 * Fetch all outlets for the current user's company
 */
export const fetchCompanyOutlets = async (
  companyId: number,
  outletId?: string | null,
): Promise<APIResponse<Outlet[]>> => {
  try {
    // outlets has no currency column of its own — currency is the company's
    // signup country's currency (companies.country_id -> supported_countries),
    // the same join user_profiles_view already uses for the single-outlet
    // case. Without this join, `currency_prefix` was always undefined for
    // company-scope/multi-outlet users, which silently fell through to a
    // hardcoded 'UGX' default wherever it was displayed.
    let query = supabase
      .from('outlets')
      .select('*, companies(country_id, supported_countries(currency_prefix))')
      .eq('company_id', companyId);

    if (outletId) {
      query = query.eq('id', outletId);
    }

    const { data, error } = await query.order('name', { ascending: true });

    if (error) {
      return { success: false, error: error.message, data: [] };
    }

    const outlets = (data || []).map((row: Record<string, unknown>) => {
      const company = row.companies as {
        supported_countries?: { currency_prefix?: string | null } | null;
      } | null;
      const currencyPrefix =
        company?.supported_countries?.currency_prefix ?? null;
      return {
        ...row,
        currency_prefix: currencyPrefix,
        companies: undefined,
      } as unknown as Outlet;
    });

    return { success: true, data: outlets };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to fetch outlets',
      data: [],
    };
  }
};

/**
 * Fetch outlets with stats (user count, menu item count)
 */
export const fetchOutletsWithStats = async (
  companyId: number,
  outletId?: string | null,
): Promise<APIResponse<OutletWithStats[]>> => {
  try {
    let query = supabase
      .from('outlets')
      .select('*')
      .eq('company_id', companyId);

    if (outletId) {
      query = query.eq('id', outletId);
    }

    const { data: outlets, error: outletsError } = await query.order('name', {
      ascending: true,
    });

    if (outletsError) {
      return { success: false, error: outletsError.message, data: [] };
    }

    // Fetch stats for each outlet in parallel
    const outletsWithStats = await Promise.all(
      (outlets || []).map(async (outlet: Outlet) => {
        const [{ count: userCount }, { count: menuItemCount }] =
          await Promise.all([
            supabase
              .from('users')
              .select('*', { count: 'exact', head: true })
              .eq('outlet_id', outlet.id)
              .eq('is_active', true),
            supabase
              .from('menu_items')
              .select('*', { count: 'exact', head: true })
              .eq('outlet_id', outlet.id),
          ]);

        return {
          ...outlet,
          user_count: userCount || 0,
          menu_item_count: menuItemCount || 0,
        } as OutletWithStats;
      }),
    );

    return { success: true, data: outletsWithStats };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch outlets with stats',
      data: [],
    };
  }
};

/**
 * Get outlet limits based on subscription package
 */
export const getOutletLimits = async (
  companyId: number,
  packageType: SubscriptionPackage,
): Promise<APIResponse<OutletLimits>> => {
  try {
    // Get current outlet count
    const { count, error } = await supabase
      .from('outlets')
      .select('*', { count: 'exact', head: true })
      .eq('company_id', companyId);

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    const maxOutlets = PACKAGE_OUTLET_LIMITS[packageType];
    const currentOutlets = count || 0;

    return {
      success: true,
      data: {
        maxOutlets: maxOutlets ?? Infinity,
        currentOutlets,
        canCreateOutlet: maxOutlets === null || currentOutlets < maxOutlets,
      },
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to get outlet limits',
      data: undefined,
    };
  }
};

/**
 * Get user limits based on subscription package
 */
export const getUserLimits = async (
  companyId: number,
  packageType: SubscriptionPackage,
): Promise<
  APIResponse<{
    maxUsers: number;
    currentUsers: number;
    canCreateUser: boolean;
  }>
> => {
  try {
    const { data: outlets, error: outletsError } = await supabase
      .from('outlets')
      .select('id')
      .eq('company_id', companyId);

    if (outletsError) {
      return { success: false, error: outletsError.message, data: undefined };
    }

    const outletIds = (outlets || []).map((outlet) => outlet.id);
    const count = outletIds.length
      ? (
          await supabase
            .from('users')
            .select('*', { count: 'exact', head: true })
            .in('outlet_id', outletIds)
            .eq('is_active', true)
        ).count
      : 0;

    const maxUsers = PACKAGE_USER_LIMITS[packageType];
    const currentUsers = count || 0;

    return {
      success: true,
      data: {
        maxUsers: maxUsers ?? Infinity,
        currentUsers,
        canCreateUser: maxUsers === null || currentUsers < maxUsers,
      },
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to get user limits',
      data: undefined,
    };
  }
};

/**
 * Create a new outlet
 */
export const createOutlet = async (
  request: CreateOutletRequest,
  packageType: SubscriptionPackage,
): Promise<APIResponse<Outlet>> => {
  try {
    // Check outlet limits first
    const limitsResult = await getOutletLimits(request.company_id, packageType);
    if (!limitsResult.success || !limitsResult.data) {
      return {
        success: false,
        error: 'Failed to check outlet limits',
        data: undefined,
      };
    }

    if (!limitsResult.data.canCreateOutlet) {
      return {
        success: false,
        error: `Outlet limit reached for your ${packageType} plan. Please upgrade to add more outlets.`,
        data: undefined,
      };
    }

    const { data, error } = await supabase
      .from('outlets')
      .insert({
        company_id: request.company_id,
        name: request.name,
        address: request.address || null,
        tin_number: request.tin_number || null,
        phone: request.phone || null,
        receipt_footnote: request.receipt_footnote || null,
        business_type: request.business_type,
        kot_tracking_enabled: request.kot_tracking_enabled ?? false,
        is_kds_enabled: request.is_kds_enabled ?? false,
      })
      .select()
      .single();

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    return { success: true, data: data as Outlet };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to create outlet',
      data: undefined,
    };
  }
};

/**
 * Update an outlet
 */
export const updateOutlet = async (
  request: UpdateOutletRequest,
): Promise<APIResponse<Outlet>> => {
  try {
    const updateData: Partial<Outlet> = {};
    if (request.name !== undefined) updateData.name = request.name;
    if (request.address !== undefined) updateData.address = request.address;
    if (request.tin_number !== undefined)
      updateData.tin_number = request.tin_number || null;
    if (request.phone !== undefined) updateData.phone = request.phone || null;
    if (request.receipt_footnote !== undefined)
      updateData.receipt_footnote = request.receipt_footnote || null;
    if (request.day_open !== undefined) updateData.day_open = request.day_open;
    if (request.business_type !== undefined) {
      updateData.business_type = request.business_type;
    }
    if (request.kot_tracking_enabled !== undefined) {
      updateData.kot_tracking_enabled = request.kot_tracking_enabled;
    }
    if (request.is_kds_enabled !== undefined) {
      updateData.is_kds_enabled = request.is_kds_enabled;
    }

    let { data, error } = await supabase
      .from('outlets')
      .update(updateData)
      .eq('id', request.id)
      .select()
      .single();

    if (
      error &&
      request.day_open !== undefined &&
      /day_open/i.test(error.message) &&
      /column/i.test(error.message)
    ) {
      const restUpdateData = { ...updateData };
      delete restUpdateData.day_open;

      const fallbackPayload = {
        ...restUpdateData,
        day_opened_at: request.day_open,
      };

      const fallbackResponse = await supabase
        .from('outlets')
        .update(fallbackPayload)
        .eq('id', request.id)
        .select()
        .single();

      data = fallbackResponse.data;
      error = fallbackResponse.error;
    }

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    return { success: true, data: data as Outlet };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to update outlet',
      data: undefined,
    };
  }
};

/**
 * Delete an outlet
 */
export const deleteOutlet = async (
  outletId: string,
): Promise<APIResponse<void>> => {
  try {
    const { error } = await supabase
      .from('outlets')
      .delete()
      .eq('id', outletId);

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    return { success: true, data: undefined };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to delete outlet',
      data: undefined,
    };
  }
};

/**
 * Get a single outlet by ID
 */
export const getOutletById = async (
  outletId: string,
): Promise<APIResponse<Outlet>> => {
  try {
    const { data, error } = await supabase
      .from('outlets')
      .select('*')
      .eq('id', outletId)
      .single();

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    return { success: true, data: data as Outlet };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to get outlet',
      data: undefined,
    };
  }
};
